import React from "react";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";

export interface ModoHistoriaFilterOptions {
  searchText: string;
  selectedMundo: string;
  selectedEtapa: string;
  vigenciaFilter: "todos" | "vigentes" | "no-vigentes";
  tieneGraficoFilter: "todos" | "con-grafico" | "sin-grafico";
}

export interface ModoHistoriaFilterProps {
  filters: ModoHistoriaFilterOptions;
  onFiltersChange: (filters: ModoHistoriaFilterOptions) => void;
  mundos?: Array<{ id: number; name: string }>;
  etapas?: Array<{ id: number; name: string; mundoId: number }>;
  placeholder?: string;
}

export const ModoHistoriaFilter: React.FC<ModoHistoriaFilterProps> = ({
  filters,
  onFiltersChange,
  mundos = [],
  etapas = [],
  placeholder = "Buscar por pregunta, respuesta, mundo, etapa...",
}) => {
  const handleSearchChange = (value: string) => {
    onFiltersChange({
      ...filters,
      searchText: value,
    });
  };

  const handleMundoChange = (value: string) => {
    onFiltersChange({
      ...filters,
      selectedMundo: value,
      selectedEtapa: "", // Limpiar etapa cuando cambia el mundo
    });
  };

  const handleEtapaChange = (value: string) => {
    onFiltersChange({
      ...filters,
      selectedEtapa: value,
    });
  };

  const handleVigenciaChange = (value: string) => {
    onFiltersChange({
      ...filters,
      vigenciaFilter: value as "todos" | "vigentes" | "no-vigentes",
    });
  };

  const handleTieneGraficoChange = (value: string) => {
    onFiltersChange({
      ...filters,
      tieneGraficoFilter: value as "todos" | "con-grafico" | "sin-grafico",
    });
  };

  // Filtrar etapas basado en el mundo seleccionado
  const getFilteredEtapas = () => {
    if (!filters.selectedMundo) {
      return etapas;
    }
    const mundoId = parseInt(filters.selectedMundo);
    const filteredEtapas = etapas.filter((etapa) => etapa.mundoId === mundoId);
    return filteredEtapas;
  };

  const clearFilters = () => {
    onFiltersChange({
      searchText: "",
      selectedMundo: "",
      selectedEtapa: "",
      vigenciaFilter: "todos",
      tieneGraficoFilter: "todos",
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        {/* Búsqueda por texto */}
        <div>
          <Label htmlFor="search" className="text-sm font-medium text-gray-700">
            Buscar
          </Label>
          <Input
            id="search"
            type="text"
            value={filters.searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={placeholder}
            className="mt-1"
          />
        </div>

        {/* Filtro por Mundo */}
        <div>
          <Label htmlFor="mundo" className="text-sm font-medium text-gray-700">
            Mundo
          </Label>
          <select
            id="mundo"
            value={filters.selectedMundo}
            onChange={(e) => handleMundoChange(e.target.value)}
            className="mt-1 w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Todos los mundos</option>
            {mundos.map((mundo) => (
              <option key={mundo.id} value={mundo.id.toString()}>
                {mundo.name}
              </option>
            ))}
          </select>
        </div>

        {/* Filtro por Etapa */}
        <div>
          <Label htmlFor="etapa" className="text-sm font-medium text-gray-700">
            Etapa
          </Label>
          <select
            id="etapa"
            value={filters.selectedEtapa}
            onChange={(e) => handleEtapaChange(e.target.value)}
            className="mt-1 w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={
              !!filters.selectedMundo && getFilteredEtapas().length === 0
            }
          >
            <option value="">
              {filters.selectedMundo
                ? getFilteredEtapas().length === 0
                  ? "No hay etapas"
                  : "Todas las etapas"
                : "Todas las etapas"}
            </option>
            {getFilteredEtapas().map((etapa) => (
              <option key={etapa.id} value={etapa.id.toString()}>
                {etapa.name}
              </option>
            ))}
          </select>
        </div>

        {/* Filtro por vigencia */}
        <div>
          <Label
            htmlFor="vigencia"
            className="text-sm font-medium text-gray-700"
          >
            Estado
          </Label>
          <select
            id="vigencia"
            value={filters.vigenciaFilter}
            onChange={(e) => handleVigenciaChange(e.target.value)}
            className="mt-1 w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="todos">Todos</option>
            <option value="vigentes">Vigentes</option>
            <option value="no-vigentes">No Vigentes</option>
          </select>
        </div>

        {/* Filtro por gráfico */}
        <div>
          <Label
            htmlFor="tieneGrafico"
            className="text-sm font-medium text-gray-700"
          >
            ¿Tiene gráfico?
          </Label>
          <select
            id="tieneGrafico"
            value={filters.tieneGraficoFilter}
            onChange={(e) => handleTieneGraficoChange(e.target.value)}
            className="mt-1 w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="todos">Todos</option>
            <option value="con-grafico">Con gráfico</option>
            <option value="sin-grafico">Sin gráfico</option>
          </select>
        </div>

        {/* Botón para limpiar filtros */}
        <div className="flex items-end">
          <button
            onClick={clearFilters}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md font-medium transition-colors"
          >
            Limpiar Filtros
          </button>
        </div>
      </div>
    </div>
  );
};
